import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
export type LightSet = 'A' | 'B';
type Point = {
  x: number;
  y: number;
};

/** Four fixed magnetic bases on the pool walls. */
const BASES: Point[] = [{
  x: 36,
  y: 105
}, {
  x: 36,
  y: 175
}, {
  x: 312,
  y: 105
}, {
  x: 312,
  y: 175
}];

/** Four charging slots in the dock beside the pool. */
const SLOTS: Point[] = [{
  x: 384,
  y: 72
}, {
  x: 384,
  y: 117
}, {
  x: 384,
  y: 162
}, {
  x: 384,
  y: 207
}];
const LED_ROWS = [1, 3, 5, 3, 1];
const SWAP_EASE = [0.65, 0, 0.35, 1] as const;

/**
 * Top-down pool plan: one set of M1 PRO lights sits on the four fixed bases while the
 * other set charges in the dock. When `installed` changes, the lights travel between
 * the bases and the dock — the bases never move.
 */
export function RotationPoolScene({
  installed
}: {
  installed: LightSet;
}) {
  const reduce = useReducedMotion();
  const charging: LightSet = installed === 'A' ? 'B' : 'A';
  const sets: LightSet[] = ['A', 'B'];
  return <svg viewBox="0 0 480 272" className="w-full" role="img" aria-labelledby="rotation-title" data-id="element-575" data-mp="ju60368xqblj">
      <title id="rotation-title" data-id="element-576" data-mp="nty2f17qjlig">
        {`Pool plan with four fixed magnetic bases. Set ${installed} is installed and lighting the pool; set ${charging} is charging in the dock.`}
      </title>
      <defs data-id="element-577" data-mp="xt5n69gsuifl">
        <clipPath id="rot-water" data-id="element-578" data-mp="xtouyxcqjqfw">
          <rect x="36" y="50" width="276" height="180" rx="14" data-id="element-579" data-mp="xqsxfa4sv3rz" />
        </clipPath>
      </defs>

      {/* Section labels */}
      <text x="174" y="18" textAnchor="middle" fill="#9CD8FF" fontSize="12" fontFamily="Barlow Condensed" fontWeight="700" letterSpacing="2" data-id="element-580" data-mp="16tbx5ldah0a0">
        {`IN THE POOL · SET ${installed}`}
      </text>
      <text x="408" y="18" textAnchor="middle" fill="#B8C6D3" fontSize="12" fontFamily="Barlow Condensed" fontWeight="700" letterSpacing="2" data-id="element-581" data-mp="1gj3vmqoiedjr">
        {`CHARGING · SET ${charging}`}
      </text>

      {/* Pool deck + water */}
      <rect x="16" y="30" width="316" height="220" rx="24" fill="#142536" stroke="#2E4A66" data-id="element-582" data-mp="1qgro19yn78yr" />
      <rect x="36" y="50" width="276" height="180" rx="14" fill="#0A3560" data-id="element-583" data-mp="10smcenjmkcq9" />
      <g clipPath="url(#rot-water)" data-id="element-584" data-mp="vuksy1iaebe9">
        {/* Light cast into the water from the installed set */}
        <motion.g key={`glow-${installed}`} initial={reduce ? false : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3,
        delay: reduce ? 0 : 0.3,
        ease: 'easeOut'
      }} data-id="element-585" data-mp="1aiyum93yvtt8">
          {BASES.map((b, i) => <g key={i} data-id="element-586" data-mp="1jlzxm0xjd4so" data-mpi={i}>
              <circle cx={b.x} cy={b.y} r="62" fill="#248DFF" opacity="0.12" data-id="element-587" data-mp="1el2ijx7toqve" />
              <circle cx={b.x} cy={b.y} r="36" fill="#248DFF" opacity="0.18" data-id="element-588" data-mp="1en6k48i91dsi" />
            </g>)}
        </motion.g>
        {/* Subtle water lines */}
        {[90, 140, 190].map(y => <path key={y} d={`M60 ${y} q 28 -6 56 0 t 56 0 t 56 0 t 56 0`} fill="none" stroke="#9CD8FF" strokeOpacity="0.12" data-id="element-589" data-mp="1oh5jizqxujgr" />)}
      </g>
      <rect x="36" y="50" width="276" height="180" rx="14" fill="none" stroke="#9CD8FF" strokeOpacity="0.45" data-id="element-590" data-mp="3qb4utl4290tg" />
      <text x="174" y="144" textAnchor="middle" fill="#9CD8FF" fillOpacity="0.8" fontSize="13" fontFamily="Barlow Condensed" fontWeight="600" letterSpacing="2" data-id="element-591" data-mp="25tdj2falagrj">
        4 FIXED POSITIONS
      </text>

      {/* Fixed magnetic bases — never move */}
      {BASES.map((b, i) => <circle key={i} cx={b.x} cy={b.y} r="15" fill="#E7ECF1" stroke="#9CB0C3" data-id="element-592" data-mp="2112lhj3bn725" data-mpi={i} />)}
      <line x1="52" y1="105" x2="70" y2="86" stroke="#9CD8FF" strokeOpacity="0.6" data-id="element-593" data-mp="1n1b9fnwd80qs" />
      <text x="73" y="84" fill="#9CD8FF" fontSize="10" fontFamily="Barlow Condensed" fontWeight="700" letterSpacing="1.5" data-id="element-594" data-mp="1r3g1wjb2urbu">
        BASE STAYS
      </text>

      {/* Charging dock */}
      <rect x="352" y="30" width="112" height="220" rx="18" fill="#0A1726" stroke="#2E4A66" data-id="element-595" data-mp="1d5zt515b6lgx" />
      {SLOTS.map((s, i) => <g key={i} data-id="element-596" data-mp="37hxt0chqa505" data-mpi={i}>
          <circle cx={s.x} cy={s.y} r="15" fill="none" stroke="#2E4A66" strokeDasharray="2 3" data-id="element-597" data-mp="37hegxcereau1" />
          <rect x="406" y={s.y - 4} width="44" height="8" rx="4" fill="#1B3048" data-id="element-598" data-mp="3ch0z809g2h9p" />
          <motion.rect key={`${charging}-${i}`} x="406" y={s.y - 4} height="8" rx="4" fill="#248DFF" initial={reduce ? false : {
        width: 8
      }} animate={{
        width: 44
      }} transition={{
        duration: reduce ? 0 : 3,
        delay: reduce ? 0 : 0.4,
        ease: 'linear'
      }} data-id="element-599" data-mp="1ce9kerls6s78" />
        </g>)}

      {/* The eight M1 PRO lights travel between bases and dock */}
      {sets.map(set => [0, 1, 2, 3].map(i => {
      const onBase = set === installed;
      const to = onBase ? BASES[i] : SLOTS[i];
      return <motion.g key={`${set}${i}`} initial={false} animate={{
        x: to.x,
        y: to.y
      }} transition={{
        duration: reduce ? 0 : 0.3,
        ease: SWAP_EASE,
        // Outgoing set leaves first, the charged set arrives right after.
        delay: reduce ? 0 : (onBase ? 0.15 : 0) + i * 0.04
      }} data-id="element-600" data-mp="jqngfi8n5lju">
              <MiniLight lit={onBase} data-id="element-601" data-mp="2hd6ebsbmoafi" />
              <text y="26" textAnchor="middle" fill={onBase ? '#E6F4FF' : '#8FA6BC'} fontSize="9" fontFamily="Barlow Condensed" fontWeight="700" data-id="element-602" data-mp="2mc99ml1t32aa">
                {`${set}${i + 1}`}
              </text>
            </motion.g>;
    }))}

      <text x="240" y="268" textAnchor="middle" fill="#B8C6D3" fontSize="11" fontFamily="Inter" data-id="element-603" data-mp="2vcnyfudzazij">
        Bases never move — only the lights change
      </text>
    </svg>;
}

/** Small top-down M1 PRO: white housing with the 1·3·5·3·1 lens array. */
function MiniLight({
  lit
}: {
  lit: boolean;
}) {
  return <g data-id="element-604" data-mp="27lig98kkfv52">
      <circle r="12" fill="#F4F7FA" stroke="#C9D3DD" data-id="element-605" data-mp="27ls7t0l9eacv" />
      <circle r="7.5" fill={lit ? '#248DFF' : '#3A5470'} data-id="element-606" data-mp="2cfd6yvyl9adn" />
      {LED_ROWS.map((n, row) => Array.from({
      length: n
    }).map((_, j) => <circle key={`${row}-${j}`} cx={(j - (n - 1) / 2) * 2.1} cy={(row - 2) * 2.1} r="0.75" fill={lit ? '#E6F4FF' : '#8FA6BC'} data-id="element-607" data-mp="1fumep43yhcx3" data-mpi={j} />))}
    </g>;
}